import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Package, CaretDown, CaretUp, PencilSimple, Trash, Plus, ChartBar, ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const scrollToForm = (e) => {
  const form = e.target.closest("[data-testid='catalog-card']")?.querySelector("form");
  if (form) form.scrollIntoView({ behavior: "smooth", block: "center" });
};

const fmt = (c) => `$${((c || 0) / 100).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const toCents = (s) => (String(s ?? "").trim() ? Math.round(parseFloat(s) * 100) || 0 : 0);
const toDollars = (c) => (c ? (c / 100).toFixed(2) : "");

const emptyProduct = { name: "", description: "", price: "", course_id: "", taxable: true };
const emptyVariation = { label: "", reason: "", price: "", is_public_price: false, is_school_seat: false, board_id: "", school_id: "" };

function Chip({ children, bg, color, border }) {
  return (
    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide whitespace-nowrap"
      style={{ background: bg, color, border: `1px solid ${border}` }}>{children}</span>
  );
}

export function CatalogCard({ catalog, onChanged }) {
  const [open, setOpen] = useState(false);
  const [boards, setBoards] = useState([]);
  const [schools, setSchools] = useState([]);
  const [courses, setCourses] = useState([]);
  const [prodForm, setProdForm] = useState(emptyProduct);
  const [editingProductId, setEditingProductId] = useState(null);
  const [varForm, setVarForm] = useState(null); // { product_id, parent_id, editing_id, parent_label, ...emptyVariation }
  const [busy, setBusy] = useState(false);

  // Scroll the inline variation form into view whenever it opens/moves — no more silent edits.
  useEffect(() => {
    if (!varForm) return;
    const t = setTimeout(() => {
      document.querySelector("[data-testid='variation-form']")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    return () => clearTimeout(t);
  }, [varForm?.editing_id, varForm?.product_id, varForm?.parent_id]); // eslint-disable-line

  useEffect(() => {
    if (open && boards.length === 0) {
      Promise.all([api.get("/boards"), api.get("/schools"), api.get("/courses")])
        .then(([b, s, c]) => { setBoards(b.data || []); setSchools(s.data || []); setCourses(c.data || []); })
        .catch(() => {});
    }
  }, [open]); // eslint-disable-line

  const reload = async () => { try { const { data } = await api.get("/admin/catalog"); onChanged(data); } catch { /* noop */ } };
  const boardName = (id) => boards.find((b) => b.id === id)?.name || "board";
  const schoolName = (id) => schools.find((s) => s.id === id)?.name || "school";
  const courseTitle = (id) => courses.find((c) => c.id === id)?.title;

  const submitProduct = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = { name: prodForm.name, description: prodForm.description, base_price_cents: toCents(prodForm.price), course_id: prodForm.course_id || null, taxable: prodForm.taxable !== false };
      if (editingProductId) await api.patch(`/admin/catalog/${editingProductId}`, { ...body, course_id: prodForm.course_id || "" });
      else await api.post("/admin/catalog", body);
      setProdForm(emptyProduct); setEditingProductId(null);
      await reload();
      toast.success(editingProductId ? "Product updated" : "Product added to the catalogue");
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const removeProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}" and all its price variations? Existing invoices keep their prices.`)) return;
    try { await api.delete(`/admin/catalog/${p.id}`); await reload(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitVariation = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = {
        label: varForm.label, reason: varForm.reason, price_cents: toCents(varForm.price),
        is_public_price: varForm.is_public_price, is_school_seat: varForm.is_school_seat,
        board_id: varForm.board_id || "", school_id: varForm.school_id || "",
      };
      if (varForm.editing_id) await api.patch(`/admin/catalog/${varForm.product_id}/variations/${varForm.editing_id}`, body);
      else await api.post(`/admin/catalog/${varForm.product_id}/variations`, { ...body, parent_id: varForm.parent_id || null });
      setVarForm(null);
      await reload();
      toast.success(varForm.editing_id ? "Variation updated" : "Variation added");
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const removeVariation = async (p, v) => {
    if (!window.confirm(`Delete variation "${v.label}" (and any sub-variations under it)?`)) return;
    try { await api.delete(`/admin/catalog/${p.id}/variations/${v.id}`); await reload(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const renderTree = (p, parentId, depth) => {
    const kids = (p.variations || []).filter((v) => (v.parent_id || null) === parentId);
    return kids.map((v) => (
      <React.Fragment key={v.id}>
        <div className="flex items-start gap-2 px-2 py-1.5 rounded-lg bg-white/70 border border-[#C4C8CE]"
          style={{ marginLeft: depth * 18 }} data-testid={`variation-row-${v.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[#000000] text-xs">↳</span>
              <span className="font-semibold text-sm">{v.label}</span>
              <span className="font-display font-bold text-sm">{fmt(v.price_cents)}</span>
              {v.is_public_price && <Chip bg="#DCFCE7" color="#166534" border="#86EFAC">Public price</Chip>}
              {v.is_school_seat && <Chip bg="#E0F2FE" color="#075985" border="#7DD3FC">School seat</Chip>}
              {v.board_id && <Chip bg="#FEF3C7" color="#92400E" border="#FDE68A">{boardName(v.board_id)}</Chip>}
              {v.school_id && <Chip bg="#FAE8FF" color="#86198F" border="#F0ABFC">{schoolName(v.school_id)}</Chip>}
            </div>
            {v.reason && <div className="text-xs text-[#000000] italic mt-0.5">{v.reason}</div>}
          </div>
          <button type="button" className="btn-ghost p-1" title="Add a sub-variation under this"
            onClick={() => setVarForm({ ...emptyVariation, product_id: p.id, parent_id: v.id, parent_label: v.label })}
            data-testid={`variation-add-child-${v.id}`}><Plus size={13} /></button>
          <button type="button" className="btn-ghost p-1" title="Edit"
            onClick={() => setVarForm({
              product_id: p.id, editing_id: v.id, parent_label: null,
              label: v.label, reason: v.reason || "", price: toDollars(v.price_cents),
              is_public_price: !!v.is_public_price, is_school_seat: !!v.is_school_seat,
              board_id: v.board_id || "", school_id: v.school_id || "",
            })}
            data-testid={`variation-edit-${v.id}`}><PencilSimple size={13} /></button>
          <button type="button" className="btn-ghost p-1" title="Delete" onClick={() => removeVariation(p, v)}
            data-testid={`variation-delete-${v.id}`}><Trash size={13} color="#FF5100" /></button>
        </div>
        {varForm && !varForm.editing_id && varForm.product_id === p.id && varForm.parent_id === v.id && variationForm(depth + 1)}
        {varForm && varForm.editing_id === v.id && variationForm(depth)}
        {renderTree(p, v.id, depth + 1)}
      </React.Fragment>
    ));
  };

  const variationForm = (depth) => (
    <form onSubmit={submitVariation} className="flex flex-col gap-2 p-3 rounded-xl border-2 border-[#A3B800] bg-white animate-pulse-once"
      style={{ marginLeft: depth * 18, boxShadow: "0 0 0 4px rgba(204,255,0,0.35)" }} data-testid="variation-form">
      <div className="text-xs font-bold uppercase tracking-[0.1em] px-2 py-1 rounded-lg self-start"
        style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D" }}>
        {varForm.editing_id ? `✏️ Editing variation "${varForm.label}"` : varForm.parent_label ? `New sub-variation under "${varForm.parent_label}"` : "New price variation"}
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        <input data-testid="variation-label" className="input-base" required placeholder="Label (e.g. School Discount)"
          value={varForm.label} onChange={(e) => setVarForm({ ...varForm, label: e.target.value })} />
        <input data-testid="variation-price" className="input-base text-right" type="number" min="0" step="0.01" placeholder="Price $ (e.g. 25.00)"
          value={varForm.price} onChange={(e) => setVarForm({ ...varForm, price: e.target.value })} />
        <input data-testid="variation-reason" className="input-base sm:col-span-1" placeholder="Reason / description"
          value={varForm.reason} onChange={(e) => setVarForm({ ...varForm, reason: e.target.value })} />
      </div>
      <div className="flex gap-4 flex-wrap items-center text-sm">
        <label className="flex items-center gap-1.5 cursor-pointer" title="Stripe checkout uses this price for the linked course">
          <input type="checkbox" checked={varForm.is_public_price} onChange={(e) => setVarForm({ ...varForm, is_public_price: e.target.checked })} data-testid="variation-public-tag" />
          Public price <span className="text-xs text-[#000000]">(Stripe checkout)</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer" title="Board auto-billing draws the per-seat price from this branch">
          <input type="checkbox" checked={varForm.is_school_seat} onChange={(e) => setVarForm({ ...varForm, is_school_seat: e.target.checked })} data-testid="variation-seat-tag" />
          School seat price <span className="text-xs text-[#000000]">(board auto-billing)</span>
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Applies to board (override)
          <select data-testid="variation-board" className="input-base normal-case tracking-normal" value={varForm.board_id} onChange={(e) => setVarForm({ ...varForm, board_id: e.target.value })}>
            <option value="">— none —</option>
            {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </label>
        <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Applies to school (override)
          <select data-testid="variation-school" className="input-base normal-case tracking-normal" value={varForm.school_id} onChange={(e) => setVarForm({ ...varForm, school_id: e.target.value })}>
            <option value="">— none —</option>
            {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
      </div>
      <div className="flex gap-2">
        <button type="submit" className="btn-primary text-sm" disabled={busy} data-testid="variation-save">{varForm.editing_id ? "Update" : "Add variation"}</button>
        <button type="button" className="btn-secondary text-sm" onClick={() => setVarForm(null)}>Cancel</button>
      </div>
    </form>
  );

  return (
    <div className="card" data-testid="catalog-card">
      <button type="button" className="w-full flex items-center justify-between bg-transparent border-0 cursor-pointer p-0" onClick={() => setOpen((o) => !o)} data-testid="catalog-toggle">
        <div className="flex items-center gap-2">
          <Package size={22} weight="duotone" color="#A3B800" />
          <div className="text-left">
            <h3 className="text-lg font-bold font-display">Master catalogue ({catalog.length})</h3>
            <p className="text-xs text-[#000000]">Every product and its price variations — quotes, invoices, checkout and seat billing all price from here</p>
          </div>
        </div>
        {open ? <CaretUp size={18} color="#52525B" /> : <CaretDown size={18} color="#52525B" />}
      </button>
      {open && (
        <div className="mt-4 flex flex-col gap-4">
          <form onSubmit={submitProduct} className="grid grid-cols-12 gap-2 items-end">
            {editingProductId && (
              <div className="col-span-12 text-xs font-bold text-[#92400E] bg-[#FEF3C7] border border-[#FCD34D] rounded-lg px-2.5 py-1.5" data-testid="catalog-editing-banner">
                ✏️ Editing "{prodForm.name}" — change the fields below, then hit Update (or ✕ to cancel)
              </div>
            )}
            <label className="col-span-12 sm:col-span-3 text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Product name
              <input data-testid="catalog-product-name" className="input-base" required value={prodForm.name} onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} />
            </label>
            <label className="col-span-12 sm:col-span-2 text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Description
              <input data-testid="catalog-product-description" className="input-base" value={prodForm.description} onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })} />
            </label>
            <label className="col-span-6 sm:col-span-1 text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1" title="Untick for tax-exempt items — no GST/HST/PST is added and the invoice shows an exempt note">Add tax
              <span className="flex items-center h-[38px]">
                <input type="checkbox" data-testid="catalog-product-taxable" checked={prodForm.taxable !== false} onChange={(e) => setProdForm({ ...prodForm, taxable: e.target.checked })} />
              </span>
            </label>
            <label className="col-span-6 sm:col-span-2 text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Base price ($)
              <input data-testid="catalog-product-price" type="number" min="0" step="0.01" className="input-base text-right" placeholder="e.g. 99.00"
                value={prodForm.price} onChange={(e) => setProdForm({ ...prodForm, price: e.target.value })} />
            </label>
            <label className="col-span-6 sm:col-span-2 text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Linked course
              <select data-testid="catalog-product-course" className="input-base normal-case tracking-normal" value={prodForm.course_id} onChange={(e) => setProdForm({ ...prodForm, course_id: e.target.value })}>
                <option value="">— none —</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </label>
            <div className="col-span-12 sm:col-span-2 flex gap-1">
              <button type="submit" className="btn-primary text-sm w-full" disabled={busy} data-testid="catalog-product-save">{editingProductId ? "Update" : "Add product"}</button>
              {editingProductId && <button type="button" className="btn-ghost text-sm" onClick={() => { setEditingProductId(null); setProdForm(emptyProduct); }}>✕</button>}
            </div>
          </form>

          <div className="flex flex-col gap-3">
            {catalog.map((p) => (
              <div key={p.id} className="rounded-xl border border-[#C4C8CE] bg-[#ECEDEF] p-2.5 flex flex-col gap-1.5" data-testid={`catalog-product-${p.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold font-display">{p.name}</span>
                  <span className="font-display font-bold text-sm">{fmt(p.base_price_cents)}</span>
                  {p.course_id && <Chip bg="#E0E7FF" color="#3730A3" border="#A5B4FC">↔ {courseTitle(p.course_id) || "course"}</Chip>}
                  {p.taxable === false && <Chip bg="#FEE2E2" color="#991B1B" border="#FCA5A5">Tax exempt</Chip>}
                  {p.description && <span className="text-xs text-[#000000]">{p.description}</span>}
                  <span className="flex-1" />
                  <button type="button" className="btn-secondary text-xs py-1 px-2 flex items-center gap-1"
                    onClick={() => setVarForm({ ...emptyVariation, product_id: p.id, parent_id: null, parent_label: null })}
                    data-testid={`catalog-add-variation-${p.id}`}><Plus size={12} weight="bold" /> Variation</button>
                  <button type="button" className="btn-ghost p-1" title="Edit this product"
                    onClick={(e) => { setEditingProductId(p.id); setProdForm({ name: p.name, description: p.description || "", price: toDollars(p.base_price_cents), course_id: p.course_id || "", taxable: p.taxable !== false }); scrollToForm(e); }}
                    data-testid={`catalog-edit-${p.id}`}><PencilSimple size={14} /></button>
                  <button type="button" className="btn-ghost p-1" onClick={() => removeProduct(p)} data-testid={`catalog-delete-${p.id}`}><Trash size={14} color="#FF5100" /></button>
                </div>
                {varForm && !varForm.editing_id && varForm.product_id === p.id && !varForm.parent_id && variationForm(0)}
                {renderTree(p, null, 0)}
                {(p.variations || []).length === 0 && <div className="text-xs text-[#000000] italic">No price variations — the base price applies. Add one (e.g. "School Discount") to price it differently for different buyers.</div>}
              </div>
            ))}
            {catalog.length === 0 && <div className="text-[#000000] text-sm italic">Catalogue is empty — add your first product (e.g. "Self-Agency Course") above.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export function SalesStatsCard() {
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState(null);

  const load = () => api.get("/admin/catalog/stats").then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { if (open && !stats) load(); }, [open]); // eslint-disable-line

  const Cell = ({ units, cents }) => (
    <td className="px-2 py-1 text-right whitespace-nowrap">{units.toLocaleString()} <span className="text-[#000000]">·</span> <span className="font-semibold">{fmt(cents)}</span></td>
  );

  return (
    <div className="card" data-testid="sales-stats-card">
      <button type="button" className="w-full flex items-center justify-between bg-transparent border-0 cursor-pointer p-0" onClick={() => setOpen((o) => !o)} data-testid="sales-stats-toggle">
        <div className="flex items-center gap-2">
          <ChartBar size={22} weight="duotone" color="#00B8D4" />
          <div className="text-left">
            <h3 className="text-lg font-bold font-display">Sales by product</h3>
            <p className="text-xs text-[#000000]">Booked (invoiced) vs realized (paid) per catalogue product — plus your accounts receivable</p>
          </div>
        </div>
        {open ? <CaretUp size={18} color="#52525B" /> : <CaretDown size={18} color="#52525B" />}
      </button>
      {open && stats && (
        <div className="mt-4 flex flex-col gap-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-[#C4C8CE] bg-white p-3">
              <div className="text-xs uppercase tracking-[0.1em] text-[#000000]">Booked (invoiced)</div>
              <div className="font-display font-extrabold text-xl" data-testid="stats-booked-total">{fmt(stats.booked_total_cents)}</div>
            </div>
            <div className="rounded-xl border border-[#C4C8CE] bg-white p-3">
              <div className="text-xs uppercase tracking-[0.1em] text-[#000000]">Realized (paid)</div>
              <div className="font-display font-extrabold text-xl" style={{ color: "#0A6B2C" }} data-testid="stats-realized-total">{fmt(stats.realized_total_cents)}</div>
            </div>
            <div className="rounded-xl border-2 p-3" style={{ borderColor: "#FDE68A", background: "#FFFBEB" }}>
              <div className="text-xs uppercase tracking-[0.1em] text-[#92400E]">Accounts receivable</div>
              <div className="font-display font-extrabold text-xl" style={{ color: "#92400E" }} data-testid="stats-receivable-total">{fmt(stats.accounts_receivable_cents)}</div>
              <div className="text-[10px] text-[#92400E]">Invoiced but not yet collected — your year-end receivable for accrual / tax reporting</div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sales-stats-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[0.1em] text-[#000000] border-b border-[#C4C8CE]">
                  <th className="px-2 py-1">Product / variation</th>
                  <th className="px-2 py-1 text-right">Booked (qty · $)</th>
                  <th className="px-2 py-1 text-right">Realized (qty · $)</th>
                  <th className="px-2 py-1 text-right">Stripe public sales</th>
                </tr>
              </thead>
              <tbody>
                {stats.products.map((p) => (
                  <React.Fragment key={p.product_id}>
                    <tr className="border-b border-[#ECEDEF] font-semibold" data-testid={`stats-product-${p.product_id}`}>
                      <td className="px-2 py-1">{p.name}{p.course_title && <span className="text-xs text-[#000000] font-normal ml-2">↔ {p.course_title}</span>}</td>
                      <Cell units={p.booked_units} cents={p.booked_cents} />
                      <Cell units={p.realized_units} cents={p.realized_cents} />
                      <td className="px-2 py-1 text-right whitespace-nowrap">{p.course_id ? <>{p.stripe_units.toLocaleString()} <span className="text-[#000000]">·</span> <span className="font-semibold">{fmt(p.stripe_cents)}</span></> : <span className="text-[#000000]">—</span>}</td>
                    </tr>
                    {p.variations.map((v) => (
                      <tr key={`${p.product_id}-${v.variation_id || "base"}`} className="border-b border-[#ECEDEF] text-[#000000]">
                        <td className="px-2 py-1 pl-6 text-xs">↳ {v.label}</td>
                        <Cell units={v.booked_units} cents={v.booked_cents} />
                        <Cell units={v.realized_units} cents={v.realized_cents} />
                        <td />
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {(stats.uncatalogued.booked_units > 0 || stats.uncatalogued.booked_cents > 0) && (
                  <tr className="border-b border-[#ECEDEF] italic text-[#000000]" data-testid="stats-uncatalogued">
                    <td className="px-2 py-1">Uncatalogued line items (ad-hoc)</td>
                    <Cell units={stats.uncatalogued.booked_units} cents={stats.uncatalogued.booked_cents} />
                    <Cell units={stats.uncatalogued.realized_units} cents={stats.uncatalogued.realized_cents} />
                    <td />
                  </tr>
                )}
                {stats.products.length === 0 && (
                  <tr><td colSpan={4} className="px-2 py-3 text-center italic text-[#000000]">No catalogue products yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <button type="button" className="btn-secondary self-start text-sm flex items-center gap-1" onClick={load} data-testid="sales-stats-refresh">
            <ArrowsClockwise size={14} /> Refresh
          </button>
        </div>
      )}
    </div>
  );
}
